import { Box, Divider, Stack, Step, StepLabel, Stepper } from "@mui/material";
import { useCallback } from "react";
import {
  AutocompleteInput,
  FormDataConsumer,
  NumberInput,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRedirect,
} from "react-admin";
import { FieldValues, SubmitHandler } from "react-hook-form";
import ExplicitValuesBoolenInput from "shared/components/inputs/ExplicitValuesBooleanInput";
import CustomDeletedDialogToolbar from "shared/components/toolbars/CustomDeletedDialogToolbar";
import { SiteContryEnum } from "shared/enums/globalEnums";
import { useGroupCertificationModalContext } from "../../hooks/useGroupCertifModal";
import {
  rteComputationVersionChoices,
  siteCountryChoices,
  siteModeChoices,
  siteOptimizerChoices,
} from "shared/enums/reactAdminChoices";
import { RteCommunicationV2ResourcesEnum, SiteResourcesEnum } from "shared/enums/resourceMappings";
import useStepper from "shared/hooks/useStepper";
import { getDataproviderCustomErrorMessage } from "shared/utils/errorHandlers";
import { siteValidations } from "../validations";
import { FormStepContainer } from "./FormStepContainer";
import { StepNavigationButtons } from "./StepNavigationButtons";
import { CountryDependentInputs } from "./inputs/CountryDependentInputs";
import { CustomSelectCountryInput } from "./inputs/CustomSelectCountryInput";

type SitesBaseFormProps = {
  isCreateForm?: boolean;
  defaultValues?: Record<string, unknown>;
};

export function SitesBaseForm({ isCreateForm = true, defaultValues = {} }: SitesBaseFormProps) {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const record = useRecordContext();
  const { activeStep, handleNext, handleBack } = useStepper();
  const { openModalAndWait } = useGroupCertificationModalContext();

  const isProduction = process.env.REACT_APP_ENVIRONNEMENT === "production";

  const steps = [
    "Basic Information",
    "Aggregation Details",
    "Location Information",
    "Network Information",
    "Capacity and Efficiency",
    "Advanced Settings",
    ...(isCreateForm ? ["Site Configuration"] : []),
  ];

  const handleSubmit: SubmitHandler<FieldValues> = useCallback(
    async (data) => {
      if (data.rte_code && isProduction && data.country === SiteContryEnum.France) {
        const { data: rteData } = await dataProvider.getOne(RteCommunicationV2ResourcesEnum.RteCommunicationConfig, {
          id: data.rte_code,
        });
        if (!rteData) {
          notify("Tranche TM site not found", { type: "error" });
          return;
        }
        data.rte_code = rteData.code_tm;
      }
      data.group_ids = data.group_ids || [];

      try {
        if (isCreateForm) {
          const createdData = await dataProvider.create(SiteResourcesEnum.Sites, { data });
          notify("Site and configuration created successfully", { type: "success" });
          if (data.country == SiteContryEnum.Finland) {
            await openModalAndWait(createdData.data.id, createdData.data.start_date);
          }
        } else {
          await dataProvider.update(SiteResourcesEnum.Sites, { id: data.id, data, previousData: record });
          notify("Site updated successfully", { type: "success" });
        }
        redirect("list", SiteResourcesEnum.Sites);
      } catch (error: any) {
        notify(getDataproviderCustomErrorMessage(error), { type: "error" });
      }
    },
    [isCreateForm, dataProvider, notify, record, redirect, isProduction, openModalAndWait],
  );

  const isLastStep = activeStep === steps.length - 1;
  const isClickable = !isCreateForm || (isCreateForm && isLastStep);

  return (
    <SimpleForm
      mode="onBlur"
      validate={siteValidations}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      toolbar={
        <CustomDeletedDialogToolbar isCreateMode={isCreateForm} resource={SiteResourcesEnum.Sites} resourceKey={"Site"}>
          <StepNavigationButtons
            activeStep={activeStep}
            steps={steps}
            handleNext={handleNext}
            handleBack={handleBack}
          />

          <SaveButton label="Submit" size="large" disabled={!isClickable} />
        </CustomDeletedDialogToolbar>
      }
    >
      <Box sx={{ width: "100%" }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Divider sx={{ mt: 4, mb: 4 }} />
        <Stack
          direction="column"
          spacing={2}
          sx={{
            justifyContent: "space-between",
            height: "100%",
            mt: 2,
            minHeight: "500px",
          }}
        >
          <Box>
            <FormStepContainer hidden={activeStep !== 0}>
              {isCreateForm && <NumberInput fullWidth source="id" validate={[required()]} />}
              <NumberInput fullWidth source="asset_id" label="Asset ID" />
              <TextInput fullWidth source="name" validate={isCreateForm ? [required()] : []} />
              <CustomSelectCountryInput fullWidth source="country" choices={siteCountryChoices} />
              <CountryDependentInputs isCreateForm={isCreateForm} />
              <ExplicitValuesBoolenInput label="IECharge" falseLabel="No" trueLabel="Yes" source="iecharge" />
            </FormStepContainer>
            <FormStepContainer hidden={activeStep !== 1}>
              <FormDataConsumer>
                {({ formData }) => {
                  if (formData.country !== SiteContryEnum.France) return <></>;
                  return (
                    <>
                      {!isProduction ? (
                        <TextInput fullWidth source="rte_code" label="Tranche TM site" validate={[required()]} />
                      ) : (
                        <ReferenceInput
                          source="rte_code"
                          reference={RteCommunicationV2ResourcesEnum.RteCommunicationConfig}
                          perPage={100}
                          filter={{
                            telemetry_field_name: "P.PROD",
                          }}
                        >
                          <AutocompleteInput optionText="code_tm" label="Tranche TM site" validate={[required()]} />
                        </ReferenceInput>
                      )}
                    </>
                  );
                }}
              </FormDataConsumer>
              <SelectInput fullWidth source="mode" choices={siteModeChoices} />
            </FormStepContainer>
            <FormStepContainer hidden={activeStep !== 2}>
              <TextInput fullWidth source="city" />
              <NumberInput fullWidth source="zipcode" />
              <NumberInput fullWidth source="latitude" />
              <NumberInput fullWidth source="longitude" />
            </FormStepContainer>
            <FormStepContainer hidden={activeStep !== 3}>
              <TextInput fullWidth source="network_ip" />
              <TextInput fullWidth source="aggregator_module_healthcheck_url" />
            </FormStepContainer>
            <FormStepContainer hidden={activeStep !== 4}>
              <NumberInput fullWidth source="max_cycles" />
              <NumberInput fullWidth source="rack_count" />
              <NumberInput fullWidth source="min_power_mw" validate={isCreateForm ? [required()] : []} />
              <NumberInput fullWidth source="max_power_mw" validate={isCreateForm ? [required()] : []} />
              <NumberInput fullWidth source="efficiency" />
              <NumberInput fullWidth source="max_soc_mwh" />
              <NumberInput fullWidth source="soh" />
              <SelectInput
                fullWidth
                source="optimizer"
                choices={siteOptimizerChoices}
                validate={isCreateForm ? [required()] : []}
              />
              <NumberInput fullWidth source="nominal_power" validate={isCreateForm ? [required()] : []} />
              <NumberInput fullWidth source="nominal_useful_energy" validate={isCreateForm ? [required()] : []} />
            </FormStepContainer>
            <FormStepContainer hidden={activeStep !== 5}>
              <NumberInput fullWidth source="useful_energy_at_1mw" validate={isCreateForm ? [required()] : []} />
            </FormStepContainer>
            <FormStepContainer hidden={!isCreateForm || activeStep !== 6}>
              <NumberInput fullWidth source="desired_configuration.power_mw" />
              <NumberInput fullWidth source="desired_configuration.efficiency_roundtrip" />
              <SelectInput
                fullWidth
                source="desired_configuration.rte_fcr_computation_version"
                label="Desired configuration for rte afrr-fcr computation"
                choices={rteComputationVersionChoices}
                parse={(value) => (value === "" || value == null ? null : Number(value))}
              />
            </FormStepContainer>
          </Box>
        </Stack>
      </Box>
    </SimpleForm>
  );
}
